import { useState, useEffect } from 'react'

/**
 * CountdownTimer - A reusable countdown timer component
 * Displays time remaining until a target date/time
 * 
 * @param {string} targetDate - ISO date string to count down to
 * @param {boolean} showSeconds - Whether to display seconds (default: true)
 * @param {string} prefix - Text to show before the timer (default: "Brackets close in")
 * @param {string} className - Additional CSS classes
 */
export default function CountdownTimer({ 
  targetDate, 
  showSeconds = true, 
  prefix = "Brackets close in",
  className = ""
}) {
  const [timeRemaining, setTimeRemaining] = useState(null)

  useEffect(() => {
    if (!targetDate) return

    const calculateTimeRemaining = () => {
      const now = new Date().getTime()
      const target = new Date(targetDate).getTime()
      const difference = target - now

      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          expired: true
        }
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((difference % (1000 * 60)) / 1000)

      return { days, hours, minutes, seconds, expired: false }
    }

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining())

    // Update every second
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining())
    }, 1000)

    return () => clearInterval(interval)
  }, [targetDate])

  if (!targetDate || !timeRemaining) {
    return null
  }

  if (timeRemaining.expired) {
    return null
  }

  // Build time parts array for display
  const buildTimeParts = () => {
    const parts = []
    
    if (timeRemaining.days > 0) {
      parts.push({ value: timeRemaining.days, label: timeRemaining.days === 1 ? 'day' : 'days' })
    }
    
    if (timeRemaining.hours > 0 || timeRemaining.days > 0) {
      parts.push({ value: timeRemaining.hours, label: timeRemaining.hours === 1 ? 'hour' : 'hours' })
    }
    
    if (timeRemaining.minutes > 0 || timeRemaining.hours > 0 || timeRemaining.days > 0) {
      parts.push({ value: timeRemaining.minutes, label: timeRemaining.minutes === 1 ? 'minute' : 'minutes' })
    }
    
    if (showSeconds && parts.length === 0) {
      parts.push({ value: timeRemaining.seconds, label: timeRemaining.seconds === 1 ? 'second' : 'seconds' })
    }
    
    return parts
  }

  const timeParts = buildTimeParts()

  return (
    <div className={className}>
      {prefix && <span>{prefix} </span>}
      {timeParts.map((part, index) => (
        <span key={index}>
          <span className="font-bold">{part.value}</span>
          <span> {part.label}</span>
          {index < timeParts.length - 1 && <span> </span>}
        </span>
      ))}
    </div>
  )
}
